import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Alert,
  Badge,
  Button,
  Container,
  Group,
  List,
  NumberInput,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  Textarea,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import { IconAlertTriangle, IconFileTypeCsv, IconUpload, IconX } from '@tabler/icons-react';

import * as Sentry from '@sentry/react';

import { LogoPaper, PageTitle } from '@/components';
import { parseCsv } from '@/components/ReportDocuments/csv';
import {
  applyCountryFix,
  buildDraftRows,
  detectColumns,
  templateCsv,
  toPayloadRows,
  type ColumnMapping,
} from '@/components/ScreeningBatchImport/screeningBatchImport';
import { JURISDICTIONS } from '@/constants/jurisdictions';
import { useReportConfigs, useScreeningBatches, useTeams } from '@/hooks';
import { ApiValidationError } from '@/services/vnext-api';
import { ScreeningBatchRowError } from '@/types';

const JURISDICTION_OPTIONS = JURISDICTIONS.map((country) => ({
  value: country.alpha2code,
  label: country.name,
}));

/** Rows shown in the preview table: 5,000-row batches must stay responsive,
 * and the problem count above the table covers the rest. */
const PREVIEW_ROWS = 200;

/** Server 422 payload | local submit failure — rendered inline, never a toast. */
type SubmitError =
  | { kind: 'row_errors'; message: string; rowErrors: ScreeningBatchRowError[] }
  | { kind: 'generic'; message: string };

export function ScreeningBatchNewPage() {
  const navigate = useNavigate();
  const { teams } = useTeams();
  const { configs } = useReportConfigs();
  const { createBatch } = useScreeningBatches();

  const [label, setLabel] = useState('');
  const [teamId, setTeamId] = useState<string | null>(null);
  const [configId, setConfigId] = useState<string | null>(null);
  const [defaultType, setDefaultType] = useState('company');
  const [maxInFlight, setMaxInFlight] = useState<number | string>(3);

  const [csvText, setCsvText] = useState('');
  const [header, setHeader] = useState<string[]>([]);
  const [data, setData] = useState<string[][]>([]);
  // Operator fixes for unresolved country cells, keyed by 1-based row index.
  const [countryFixes, setCountryFixes] = useState<Record<number, string>>({});
  const [submitError, setSubmitError] = useState<SubmitError | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const screeningConfigs = useMemo(
    () => configs.filter((config) => config.pipelineMode === 'screening_only' && config.isActive),
    [configs]
  );

  // The rows decide the ambiguous "Type" header (a vessel file's type
  // column holds "Tanker", not a subject type).
  const mapping: ColumnMapping = useMemo(() => detectColumns(header, data), [header, data]);

  const drafts = useMemo(() => {
    if (data.length === 0) {
      return [];
    }
    // Apply the operator's country fix-ups to the raw cells first so
    // validation (incl. duplicate detection) sees the corrected values.
    const jurisdictionCol = mapping.jurisdiction;
    const patched =
      jurisdictionCol === undefined
        ? data
        : data.map((row, i) => {
            const fix = countryFixes[i + 1];
            if (!fix) {
              return row;
            }
            const next = [...row];
            next[jurisdictionCol] = applyCountryFix(next[jurisdictionCol] ?? '', fix);
            return next;
          });
    return buildDraftRows(patched, mapping, defaultType);
  }, [data, mapping, countryFixes, defaultType]);

  const problemCount = drafts.filter((draft) => draft.problems.length > 0).length;
  // The name is the one required column: a country (a vessel's flag) is
  // optional, and the IMO a vessel needs is checked per row.
  const missingColumns = mapping.subject_name === undefined && header.length > 0 ? ['name'] : [];
  const previewDrafts = drafts.slice(0, PREVIEW_ROWS);

  const loadCsv = (text: string) => {
    const rows = parseCsv(text).filter((row) => row.some((cell) => cell.trim() !== ''));
    setHeader(rows[0] ?? []);
    setData(rows.slice(1));
    setCountryFixes({});
    setSubmitError(null);
  };

  const downloadTemplate = () => {
    // Client-side blob — the canonical headers, nothing to guess.
    const url = URL.createObjectURL(new Blob([templateCsv()], { type: 'text/csv' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'screening-batch-template.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const handleDrop = async (files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }
    const text = await file.text();
    setCsvText(text);
    loadCsv(text);
  };

  const canSubmit =
    label.trim() !== '' &&
    teamId !== null &&
    configId !== null &&
    drafts.length > 0 &&
    problemCount === 0 &&
    missingColumns.length === 0 &&
    !submitting;

  const handleSubmit = async () => {
    if (!teamId || !configId) {
      return;
    }
    setSubmitting(true);
    setSubmitError(null);
    try {
      const created = await createBatch({
        team_id: teamId,
        report_config_id: configId,
        label: label.trim(),
        max_in_flight: typeof maxInFlight === 'number' ? maxInFlight : 3,
        default_subject_type: defaultType,
        rows: toPayloadRows(drafts),
      });
      navigate(`/admin/screening-batches/${created.id}`);
    } catch (err) {
      Sentry.captureException(err);
      if (err instanceof ApiValidationError) {
        const rowErrors = (err.detail.row_errors as ScreeningBatchRowError[]) ?? [];
        setSubmitError({
          kind: 'row_errors',
          message: String(err.detail.message ?? err.message),
          rowErrors,
        });
      } else {
        setSubmitError({
          kind: 'generic',
          message: err instanceof Error ? err.message : String(err),
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container size="xl" py="md">
      <Stack>
        <PageTitle title="New Screening Batch" />

        <LogoPaper p={16}>
          <Stack>
            <Group grow align="flex-start">
              <TextInput
                label="Batch label"
                placeholder="e.g. ACME Corp Batch 1"
                value={label}
                onChange={(e) => setLabel(e.currentTarget.value)}
                required
              />
              <Select
                label="Client team"
                description="Reports are created in this team"
                placeholder="Select team"
                data={teams.map((team) => ({ value: String(team.id), label: team.name }))}
                value={teamId}
                onChange={setTeamId}
                searchable
                required
                data-testid="batch-team-select"
              />
              <Select
                label="Report config"
                description="Basic-screening (screening_only) configs only"
                placeholder={
                  screeningConfigs.length === 0 ? 'No screening_only configs' : 'Select config'
                }
                data={screeningConfigs.map((config) => ({
                  value: config.id,
                  label: config.name,
                }))}
                value={configId}
                onChange={setConfigId}
                required
                data-testid="batch-config-select"
              />
            </Group>
            <Group align="flex-end">
              <Stack gap={4}>
                <Text size="sm" fw={500}>
                  Default subject type
                </Text>
                <SegmentedControl
                  data={[
                    { value: 'company', label: 'Company' },
                    { value: 'person', label: 'Person' },
                    { value: 'vessel', label: 'Vessel' },
                  ]}
                  value={defaultType}
                  onChange={setDefaultType}
                  data-testid="batch-default-type"
                />
              </Stack>
              <Tooltip label="Max reports processing at once — protects production responsiveness">
                <NumberInput
                  label="Dispatch window"
                  min={1}
                  max={100}
                  value={maxInFlight}
                  onChange={setMaxInFlight}
                  w={140}
                />
              </Tooltip>
            </Group>
          </Stack>
        </LogoPaper>

        <LogoPaper p={16}>
          <Stack>
            <Group justify="space-between" align="center">
              <Text fw={500}>Subjects CSV</Text>
              <Button
                variant="subtle"
                size="compact-sm"
                onClick={downloadTemplate}
                data-testid="batch-template-download"
              >
                Download blank template
              </Button>
            </Group>
            <Text size="sm" c="dimmed">
              Required column: name. Optional: country or flag (alpha-2 codes or country names;
              several in one cell split with ; , or |), reference, subject type
              (person/company/vessel — overrides the default above per row), date of birth, address,
              registration/VAT number, IMO number (required on vessel rows), additional info.
              Identity details sharpen screening decisions.
            </Text>
            <Dropzone
              onDrop={handleDrop}
              accept={{ 'text/csv': ['.csv'] }}
              multiple={false}
              maxSize={5 * 1024 ** 2}
            >
              <Group justify="center" gap="xl" mih={80} style={{ pointerEvents: 'none' }}>
                <Dropzone.Accept>
                  <IconUpload size={32} />
                </Dropzone.Accept>
                <Dropzone.Reject>
                  <IconX size={32} />
                </Dropzone.Reject>
                <Dropzone.Idle>
                  <IconFileTypeCsv size={32} />
                </Dropzone.Idle>
                <Text c="dimmed">Drop a .csv here or click to pick one</Text>
              </Group>
            </Dropzone>
            <Textarea
              label="Or paste CSV text"
              autosize
              minRows={3}
              maxRows={8}
              value={csvText}
              onChange={(e) => setCsvText(e.currentTarget.value)}
              onBlur={() => (csvText.trim() ? loadCsv(csvText) : undefined)}
            />
            {header.length > 0 ? (
              <Group gap="xs">
                <Text size="sm" c="dimmed">
                  Detected columns:
                </Text>
                {(Object.entries(mapping) as [string, number][]).map(([field, index]) => (
                  <Badge key={field} variant="light">
                    {field} ← “{header[index]}”
                  </Badge>
                ))}
              </Group>
            ) : null}
            {missingColumns.length > 0 ? (
              <Alert color="red" icon={<IconAlertTriangle size={16} />}>
                Could not detect required column(s): {missingColumns.join(', ')}. Rename the CSV
                header (e.g. “Name”, “Company” or “Vessel Name”) and re-load.
              </Alert>
            ) : null}
          </Stack>
        </LogoPaper>

        {drafts.length > 0 ? (
          <LogoPaper p={16}>
            <Stack>
              <Group justify="space-between">
                <Text fw={500}>
                  {drafts.length} subject{drafts.length === 1 ? '' : 's'}
                  {problemCount > 0 ? ` — ${problemCount} with problems` : ' — all valid'}
                </Text>
                <Button onClick={handleSubmit} disabled={!canSubmit} loading={submitting}>
                  Create batch
                </Button>
              </Group>

              {submitError ? (
                <Alert
                  color="red"
                  title={submitError.message}
                  icon={<IconAlertTriangle size={16} />}
                >
                  {submitError.kind === 'row_errors' ? (
                    <List size="sm">
                      {submitError.rowErrors.map((rowError) => (
                        <List.Item key={`${rowError.row}-${rowError.field}`}>
                          Row {rowError.row} ({rowError.field}): {rowError.message}
                        </List.Item>
                      ))}
                    </List>
                  ) : null}
                </Alert>
              ) : null}

              {drafts.length > PREVIEW_ROWS ? (
                <Text size="sm" c="dimmed" data-testid="batch-preview-truncated">
                  Showing the first {PREVIEW_ROWS} of {drafts.length} rows; the count above covers
                  every row.
                </Text>
              ) : null}

              <Table.ScrollContainer minWidth={860}>
                <Table>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>#</Table.Th>
                      <Table.Th>Name</Table.Th>
                      <Table.Th>Country / Flag</Table.Th>
                      <Table.Th>Reference</Table.Th>
                      <Table.Th>Type</Table.Th>
                      <Table.Th>DOB</Table.Th>
                      <Table.Th>IMO</Table.Th>
                      <Table.Th>Problems</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {previewDrafts.map((draft) => (
                      <Table.Tr
                        key={draft.index}
                        bg={
                          draft.problems.length > 0 ? 'var(--mantine-color-red-light)' : undefined
                        }
                      >
                        <Table.Td>{draft.index}</Table.Td>
                        <Table.Td>{draft.subjectName || <Text c="red">missing</Text>}</Table.Td>
                        <Table.Td>
                          {draft.unresolvedJurisdictions.length > 0 ? (
                            // Only a cell that holds something we cannot read
                            // asks for a fix; an empty cell is simply no flag.
                            <Select
                              placeholder={`Fix “${draft.unresolvedJurisdictions.join('; ')}”`}
                              data={JURISDICTION_OPTIONS}
                              searchable
                              size="xs"
                              onChange={(value) =>
                                value &&
                                setCountryFixes((prev) => ({ ...prev, [draft.index]: value }))
                              }
                            />
                          ) : (
                            draft.jurisdictions.join(', ') || '—'
                          )}
                        </Table.Td>
                        <Table.Td>{draft.reference || '—'}</Table.Td>
                        <Table.Td>{draft.subjectType || defaultType}</Table.Td>
                        <Table.Td>{draft.dateOfBirth || '—'}</Table.Td>
                        <Table.Td>{draft.imo ?? (draft.imoNumber || '—')}</Table.Td>
                        <Table.Td>
                          {draft.problems.length > 0 ? (
                            <Text size="sm" c="red">
                              {draft.problems.join('; ')}
                            </Text>
                          ) : (
                            <Text size="sm" c="green">
                              ok
                            </Text>
                          )}
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Table.ScrollContainer>
            </Stack>
          </LogoPaper>
        ) : null}
      </Stack>
    </Container>
  );
}
